
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 5,
  layout: 'guide',
  description: 'Install and use the Plaid CLI to access your financial data from the terminal'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Plaid CLI' subtitle={metadata.description} mdxType="Header" />
    <p>{`The Plaid CLI is a command-line interface for the `}<a parentName="p" {...{
        "href": "/docs/api"
      }}>{`Plaid API`}</a>{`. Install once and start making API calls — no client libraries to import, no request objects to construct, no response types to parse.`}</p>
    <p>{`The CLI works for both developers and AI agents:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`For developers:`}</strong>{` readable table output in the terminal, with commands that mirror the API structure`}</li>
      <li parentName="ul"><strong parentName="li">{`For agents:`}</strong>{` structured JSON output via `}<inlineCode parentName="li">{`--json`}</inlineCode>{`, clean separation of data on stdout and diagnostics on stderr, and predictable exit codes`}</li>
    </ul>
    <p>{`The CLI currently supports `}<a parentName="p" {...{
        "href": "/docs/balance/"
      }}>{`Balance`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/transactions/"
      }}>{`Transactions`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/investments/"
      }}>{`Investments`}</a>{`, and `}<a parentName="p" {...{
        "href": "/docs/liabilities/"
      }}>{`Liabilities`}</a>{`.
Use it to link a financial institution, check balances, pull transactions, view holdings, and more, all without writing any code.`}</p>
    <Callout mdxType="Callout">
      <p>{`Use `}<inlineCode parentName="p">{`plaid feedback`}</inlineCode>{` to report issues or suggest improvements.`}</p>
    </Callout>
    <h4 {...{
      "id": "installing-the-cli"
    }}>{`Installing the CLI`}</h4>
    <p>{`The CLI is distributed via `}<a parentName="p" {...{
        "href": "https://brew.sh/"
      }}>{`Homebrew`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Install via Homebrew",
        "switcher": "false",
        "#Install": true,
        "via": true,
        "Homebrew": true
      }}>{`brew install plaid/plaid-cli/plaid
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Update to the latest version",
        "switcher": "false",
        "#Update": true,
        "to": true,
        "the": true,
        "latest": true,
        "version": true
      }}>{`brew update && brew upgrade plaid
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Verify the installation",
        "switcher": "false",
        "#Verify": true,
        "the": true,
        "installation": true
      }}>{`plaid --version
`}</code></pre>
    <h4 {...{
      "id": "getting-started"
    }}>{`Getting started`}</h4>
    <h5 {...{
      "id": "creating-an-account"
    }}>{`Creating an account`}</h5>
    <p>{`If you don't have a Plaid account yet, sign up directly from the CLI:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sign up for a Plaid account",
        "switcher": "false",
        "#Sign": true,
        "up": true,
        "for": true,
        "a": true,
        "Plaid": true,
        "account": true
      }}>{`plaid register
`}</code></pre>
    <p>{`This opens the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/"
      }}>{`Dashboard`}</a>{` signup page in your browser. After creating and verifying your account, log in:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Log in to the Dashboard",
        "switcher": "false",
        "#Log": true,
        "in": true,
        "to": true,
        "the": true,
        "Dashboard": true
      }}>{`plaid login
`}</code></pre>
    <p>{`This opens a browser for you to authenticate with the Dashboard. Once complete, the CLI stores your tokens locally and fetches your API keys automatically. Tokens are refreshed automatically by subsequent commands.`}</p>
    <h5 {...{
      "id": "signing-up-for-the-trial-plan"
    }}>{`Signing up for the Trial plan`}</h5>
    <p>{`By default, new accounts have access to the `}<a parentName="p" {...{
        "href": "/docs/sandbox/"
      }}>{`Sandbox environment`}</a>{`. To connect to real financial institutions, apply for a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#trial-plans"
      }}>{`Trial plan`}</a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Open the Trial plan application",
        "switcher": "false",
        "#Open": true,
        "the": true,
        "Trial": true,
        "plan": true,
        "application": true
      }}>{`plaid trial
`}</code></pre>
    <p>{`This opens the Trial plan application form in your browser. Once your application is approved, refresh your locally stored API keys to start using Production:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Refresh API keys after Trial plan approval",
        "switcher": "false",
        "#Refresh": true,
        "API": true,
        "keys": true,
        "after": true,
        "Trial": true,
        "plan": true,
        "approval": true
      }}>{`plaid keys fetch
`}</code></pre>
    <h4 {...{
      "id": "linking-an-item"
    }}>{`Linking an Item`}</h4>
    <p>{`The CLI is organized around `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/#item"
      }}>{`Items`}</a>{`, which represent logins at financial institutions. Product commands read data from one Item (`}<inlineCode parentName="p">{`--item`}</inlineCode>{`) or all linked Items (`}<inlineCode parentName="p">{`--all`}</inlineCode>{`).`}</p>
    <p>{`To connect a real institution, use Hosted Link in your browser:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Open Link to create an Item",
        "switcher": "false",
        "#Open": true,
        "Link": true,
        "to": true,
        "create": true,
        "an": true,
        "Item": true
      }}>{`plaid link
`}</code></pre>
    <p>{`This opens `}<a parentName="p" {...{
        "href": "/docs/link/"
      }}>{`Plaid Link`}</a>{` in your browser. Search for your financial institution, log in with your credentials, and select the accounts you want to connect. When the flow completes, the CLI automatically saves the access token and Item ID to your config so you can query product data immediately.`}</p>
    <p><inlineCode parentName="p">{`plaid link`}</inlineCode>{` accepts a `}<inlineCode parentName="p">{`--products`}</inlineCode>{` flag to choose the product set for the
linked Item:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Link an Item for Transactions and Liabilities use cases",
        "switcher": "false",
        "#Link": true,
        "an": true,
        "Item": true,
        "for": true,
        "Transactions": true,
        "and": true,
        "Liabilities": true,
        "use": true,
        "cases": true
      }}>{`plaid link --products transactions,liabilities
`}</code></pre>
    <p><inlineCode parentName="p">{`plaid item link`}</inlineCode>{` is an equivalent namespace form when you want to stay inside
the `}<inlineCode parentName="p">{`item`}</inlineCode>{` command tree.`}</p>
    <p>{`To see which Items you have linked:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #List linked Items in the current environment",
        "switcher": "false",
        "#List": true,
        "linked": true,
        "Items": true,
        "in": true,
        "the": true,
        "current": true,
        "environment": true
      }}>{`plaid item list
`}</code></pre>
    <h4 {...{
      "id": "creating-a-sandbox-item"
    }}>{`Creating a Sandbox Item`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "/docs/sandbox/"
      }}>{`Sandbox environment`}</a>{` allows you to test without connecting a real financial institution.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Create a Sandbox Item directly",
        "switcher": "false",
        "#Create": true,
        "a": true,
        "Sandbox": true,
        "Item": true,
        "directly": true
      }}>{`plaid config set --env sandbox
plaid sandbox link
`}</code></pre>
    <p>{`You can choose a Sandbox product scenario without opening Link:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Create Sandbox Items for specific product scenarios",
        "switcher": "false",
        "#Create": true,
        "Sandbox": true,
        "Items": true,
        "for": true,
        "specific": true,
        "product": true,
        "scenarios": true
      }}>{`plaid sandbox link --products balance
plaid sandbox link --products transactions,liabilities
plaid sandbox link --products investments
`}</code></pre>
    <h4 {...{
      "id": "querying-personal-financial-management-pfm-product-data"
    }}>{`Querying personal financial management (PFM) product data`}</h4>
    <p>{`With one linked Item stored locally, the product commands use it by default.
When you have multiple linked Items, pass `}<inlineCode parentName="p">{`--item <item-id-or-alias>`}</inlineCode>{` for one Item or `}<inlineCode parentName="p">{`--all`}</inlineCode>{` to view every linked Item.`}</p>
    <p>{`Once you have linked an Item, the main product commands are:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Check account balances",
        "switcher": "false",
        "#Check": true,
        "account": true,
        "balances": true
      }}>{`plaid balance
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Sample output",
        "switcher": "false",
        "#Sample": true,
        "output": true
      }}>{`ACCOUNT                            AVAILABLE  CURRENT   CURRENCY
Plaid Checking (0000)              100.00     110.00    USD
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #List recent transactions",
        "switcher": "false",
        "#List": true,
        "recent": true,
        "transactions": true
      }}>{`plaid transactions list --count 5
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Sample output",
        "switcher": "false",
        "#Sample": true,
        "output": true
      }}>{`DATE        MERCHANT                   AMOUNT   CATEGORY                                  ACCOUNT
2026-04-24  Tectra Inc                 500.00   Food and Drink > Restaurants              Plaid Credit Card (3333)
2026-04-23  AUTOMATIC PAYMENT - THANK  2078.50  Payment                                   Plaid Credit Card (3333)
2026-04-23  KFC                        500.00   Food and Drink > Restaurants > Fast Food  Plaid Credit Card (3333)
2026-04-23  Madison Bicycle Shop       500.00   Shops > Sporting Goods                    Plaid Credit Card (3333)
2026-04-14  Uber                       5.40     Travel > Taxi                             Plaid Checking (0000)
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sync transactions incrementally",
        "switcher": "false",
        "#Sync": true,
        "transactions": true,
        "incrementally": true
      }}>{`plaid transactions sync
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Sample output",
        "switcher": "false",
        "#Sample": true,
        "output": true
      }}>{`Synced: 4 added, 0 modified, 0 removed

New transactions:
DATE        MERCHANT         AMOUNT
2026-04-14  Uber             5.40
2026-04-12  United Airlines  -500.00
2026-04-11  McDonald's       12.00
2026-04-11  Starbucks        4.33
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Get investment holdings",
        "switcher": "false",
        "#Get": true,
        "investment": true,
        "holdings": true
      }}>{`plaid investments holdings
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Sample output",
        "switcher": "false",
        "#Sample": true,
        "output": true
      }}>{`ACCOUNT            SECURITY                        TICKER               QUANTITY    PRICE     VALUE
Plaid 401k (6666)  Achillion Pharmaceuticals Inc.  ACHN                 1           2.11      2.11
Plaid IRA (5555)   Nflx Feb 01'18 $355 Call        NFLX180201C00355000  10000       0.01      110.00
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Get investment transactions",
        "switcher": "false",
        "#Get": true,
        "investment": true,
        "transactions": true
      }}>{`plaid investments transactions --start-date 2026-04-23
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Sample output",
        "switcher": "false",
        "#Sample": true,
        "output": true
      }}>{`DATE        NAME                                TICKER               TYPE  QUANTITY           AMOUNT    ACCOUNT
2026-04-24  BUY Achillion Pharmaceuticals Inc.  ACHN                 buy   0.520877874205698  1.10      Plaid 401k (6666)
2026-04-23  BUY NFLX DERIVATIVE                 NFLX180201C00355000  buy   4211.152345617756  46.32     Plaid IRA (5555)
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Get liabilities",
        "switcher": "false",
        "#Get": true,
        "liabilities": true
      }}>{`plaid liabilities
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Sample output",
        "switcher": "false",
        "#Sample": true,
        "output": true
      }}>{`Credit
ACCOUNT                   CURRENT  LIMIT    MIN PAYMENT  NEXT DUE    LAST STATEMENT  OVERDUE
Plaid Credit Card (3333)  410.00   2000.00  20.00        2020-05-28  1708.77         no

Student
ACCOUNT                    CURRENT   MIN PAYMENT  NEXT DUE    RATE   EXPECTED PAYOFF
Plaid Student Loan (7777)  65262.00  25.00        2019-05-28  5.25%  2032-07-28

Mortgage
ACCOUNT                CURRENT   NEXT PAYMENT  NEXT DUE    RATE   TERM
Plaid Mortgage (8888)  56302.06  3141.54       2019-11-15  3.99%  30 year
`}</code></pre>
    <h4 {...{
      "id": "json-output-for-agents-and-scripts"
    }}>{`JSON output for agents and scripts`}</h4>
    <p>{`All commands support the `}<inlineCode parentName="p">{`--json`}</inlineCode>{` flag for structured, machine-readable output. This makes the CLI a natural fit for AI agents and shell scripts.`}</p>
    <p>{`In `}<inlineCode parentName="p">{`--json`}</inlineCode>{` mode:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`stdout`}</inlineCode>{` contains the primary JSON result`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`stderr`}</inlineCode>{` contains diagnostics`}</li>
      <li parentName="ul">{`browser-based commands still open the browser when required`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Get balances as JSON",
        "switcher": "false",
        "#Get": true,
        "balances": true,
        "as": true,
        "JSON": true
      }}>{`plaid balance --json
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample output",
        "switcher": "false",
        "#Sample": true,
        "output": true
      }}>{`{
  "accounts": [
    {
      "account_id": "zNGwXw3...",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "type": "depository",
      "subtype": "checking",
      "mask": "0000",
      "balances": {
        "available": 100,
        "current": 110,
        "limit": null,
        "iso_currency_code": "USD"
      }
    },
    {
      "account_id": "BpMKbK7...",
      "name": "Plaid Saving",
      "official_name": "Plaid Silver Standard 0.1% Interest Saving",
      "type": "depository",
      "subtype": "savings",
      "mask": "1111",
      "balances": {
        "available": 200,
        "current": 210,
        "limit": null,
        "iso_currency_code": "USD"
      }
    }
  ],
  "item": {
    "item_id": "...",
    "institution_id": "ins_56"
  }
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Pipe JSON output to other tools",
        "switcher": "false",
        "#Pipe": true,
        "JSON": true,
        "output": true,
        "to": true,
        "other": true,
        "tools": true
      }}>{`plaid balance --json | jq '.accounts[0].name'
"Plaid Checking"
`}</code></pre>
    <h4 {...{
      "id": "command-guide"
    }}>{`Command guide`}</h4>
    <p>{`Run `}<inlineCode parentName="p">{`plaid --help`}</inlineCode>{` for the full command tree, or `}<inlineCode parentName="p">{`plaid <command> --help`}</inlineCode>{` for
details on a specific command.`}</p>
    <h5 {...{
      "id": "access"
    }}>{`Access`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Command`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid register`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Register an account in the Dashboard`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid login`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Log in to the Dashboard`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid trial`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Start the Trial plan onboarding in the Dashboard`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid logout`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Remove stored Dashboard credentials`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "products"
    }}>{`Products`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Command`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid link`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Open Link to create an Item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid item list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List linked Items in the current environment`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid item get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Show an Item and the accounts it contains`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid item remove`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Remove a linked Item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid balance`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get balance data`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid transactions list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid transactions sync`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Sync transactions incrementally`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid investments holdings`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get investments holdings`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid investments transactions`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get investments transactions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid liabilities`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get liabilities`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid sandbox link`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a Sandbox Item`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "configuration"
    }}>{`Configuration`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Command`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid config`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Show Plaid CLI config status`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid config set`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Manually modify config`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid teams list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List available Dashboard teams`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid teams choose`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Interactively choose the active team`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid keys fetch`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fetch API keys from the Dashboard`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "utilities"
    }}>{`Utilities`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Command`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid feedback`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Submit feedback about the CLI`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid help`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Help about any command`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "configuration-1"
    }}>{`Configuration`}</h4>
    <p>{`Credentials are read with this precedence: `}<strong parentName="p">{`flags > environment variables > config file`}</strong>{`.`}</p>
    <p>{`Each environment (Sandbox, Production) stores its own credentials separately. Switching environments preserves all stored data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Managing credentials",
        "switcher": "false",
        "#Managing": true,
        "credentials": true
      }}>{`# Set credentials (secret is prompted interactively)
plaid config set --client-id <your-client-id> --env sandbox

# Switch to production
plaid config set --env production

# View current config (secrets are masked)
plaid config
`}</code></pre>
    <h5 {...{
      "id": "environment-variables"
    }}>{`Environment variables`}</h5>
    <p>{`For scripted and automated usage, credentials can be set via environment variables:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Variable`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PLAID_CLIENT_ID`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plaid client ID`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PLAID_SECRET`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plaid secret`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PLAID_ENV`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Environment (`}<inlineCode parentName="td">{`sandbox`}</inlineCode>{` or `}<inlineCode parentName="td">{`production`}</inlineCode>{`)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PLAID_ACCESS_TOKEN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Access token`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "feedback"
    }}>{`Feedback`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Submit feedback, bug reports, or feature requests",
        "switcher": "false",
        "#Submit": true,
        "feedback,": true,
        "bug": true,
        "reports,": true,
        "or": true,
        "feature": true,
        "requests": true
      }}>{`plaid feedback "The transactions list command is great!"
`}</code></pre>
    <p>{`You can also run `}<inlineCode parentName="p">{`plaid feedback`}</inlineCode>{` without a message to enter feedback interactively.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "installing-the-cli",
        level: 4,
        title: "Installing the CLI",
      },
{
        id: "getting-started",
        level: 4,
        title: "Getting started",
      },
{
        id: "linking-an-item",
        level: 4,
        title: "Linking an Item",
      },
{
        id: "creating-a-sandbox-item",
        level: 4,
        title: "Creating a Sandbox Item",
      },
{
        id: "querying-personal-financial-management-pfm-product-data",
        level: 4,
        title: "Querying personal financial management (PFM) product data",
      },
{
        id: "json-output-for-agents-and-scripts",
        level: 4,
        title: "JSON output for agents and scripts",
      },
{
        id: "command-guide",
        level: 4,
        title: "Command guide",
      },
{
        id: "configuration-1",
        level: 4,
        title: "Configuration",
      },
{
        id: "feedback",
        level: 4,
        title: "Feedback",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
